import api from '../services/api';

/**
 * Upload a rider document via the backend's signed-URL endpoint.
 *
 * Replaces a direct-to-S3 upload that used a hardcoded AWS Cognito Identity
 * Pool. That pool was decommissioned along with the rest of our AWS footprint,
 * so every upload failed with:
 *
 *   IdentityPool 'us-east-1:63fcadef-...' not found
 *
 * which surfaced only at the point of submitting the form, after all the
 * fields had been filled in.
 *
 * The backend already held real GCS credentials and already issued signed URLs
 * for exactly this. Going through it means no cloud credentials in the browser
 * bundle at all: the server signs a one-off URL valid for fifteen minutes, and
 * we PUT the file straight to it.
 */

const ALLOWED = {
  'image/jpeg': true,
  'image/png': true,
  'image/webp': true,
  'application/pdf': true,
};

const MAX_BYTES = 10 * 1024 * 1024; // matches the server's limit

/**
 * @param {File} file
 * @param {'driver_license'|'passport'} docType
 * @returns {Promise<string>} the public URL to store on the rider profile
 */
export async function uploadRiderDocument(file, docType) {
  if (!file) return null;

  if (!ALLOWED[file.type]) {
    throw new Error(
      `${file.name} is a ${file.type || 'unknown'} file. Use JPG, PNG, WEBP or PDF.`,
    );
  }
  // Checked here as well as on the server so the failure is immediate rather
  // than after uploading several megabytes.
  if (file.size > MAX_BYTES) {
    throw new Error(
      `${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is 10 MB.`,
    );
  }

  const { data } = await api.post('/v1/uploads/admin/rider-document/signed-url', {
    docType,
    contentType: file.type,
    fileName: file.name,
    fileSizeBytes: file.size,
  });

  const { uploadUrl, publicUrl } = data?.data ?? data ?? {};
  if (!uploadUrl || !publicUrl) {
    throw new Error('The server did not return an upload URL.');
  }

  // Straight to Google Cloud Storage. Content-Type must match what the URL was
  // signed for, or GCS rejects the signature.
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  if (!res.ok) {
    throw new Error(
      `Upload failed (${res.status}). The link may have expired — try again.`,
    );
  }

  return publicUrl;
}
